import React, { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { publicService } from '../services/publicService';
import SchedulingFlow, { SchedulingApi } from '../components/scheduling/SchedulingFlow';

/**
 * Public self-scheduling page, by patient uuid. The UI lives in
 * <SchedulingFlow>; this page supplies the API.
 *
 * ?meeting_type_id and ?therapist_id are a clinician's decision, made in the
 * clinic app before the patient got here. When present they are carried on
 * every call, so the page books what was chosen rather than what the
 * patient's own state says is due next - a clinician arranging an extra
 * appointment outside the usual sequence is a normal thing to do.
 *
 * therapist_id absent alongside a meeting type means the POOL, which is a
 * decision too, and not the same as "work it out".
 *
 * ?by is the signed marker naming the clinician who issued the link, carried
 * through to the booking so the meeting records that THEY arranged it rather
 * than the patient booking of their own accord. Opaque here: it is verified
 * server-side, and a patient who edits it simply books as themselves.
 */
const PatientSchedulePage: React.FC = () => {
  const { patientUuid } = useParams<{ patientUuid: string }>();
  const [search] = useSearchParams();
  const num = (v: string | null) => (v && /^\d+$/.test(v) ? Number(v) : undefined);
  const chosenTypeId = num(search.get('meeting_type_id'));
  const chosenTherapistId = num(search.get('therapist_id'));
  const issuedBy = search.get('by') || undefined;
  // A clinician arranging ANOTHER meeting, and which one they meant.
  // `book` forces the booking screen past an existing appointment;
  // `meeting_id` says which to manage when the patient has more than one.
  const forceBook = search.get('book') === 'true';
  const chosenMeetingId = num(search.get('meeting_id'));

  const api: SchedulingApi = useMemo(() => ({
    getContext: () =>
      publicService.getScheduleContext(
        patientUuid!, chosenTypeId, chosenTherapistId,
        chosenMeetingId, forceBook),
    getFirstAvailableMonth: (meetingTypeId, meetingId) =>
      publicService.getFirstAvailableMonth(
        patientUuid!, meetingTypeId, meetingId, chosenTherapistId),
    getAvailableSlots: (meetingTypeId, from, to, meetingId) =>
      publicService.getAvailableSlots(
        patientUuid!, meetingTypeId, from, to, meetingId, chosenTherapistId),
    bookMeeting: (data) => publicService.bookMeeting(patientUuid!, {
      ...data,
      // The chosen clinician wins over the slot's own, which is only a
      // fallback for a pool booking.
      resource_id: chosenTherapistId ? String(chosenTherapistId) : data.resource_id,
      by: issuedBy,
    }),
    rescheduleMeeting: (meetingId, data) => publicService.rescheduleMeeting(patientUuid!, meetingId, data),
    cancelMeeting: (meetingId, data) => publicService.cancelMeeting(patientUuid!, meetingId, data),
  }), [patientUuid, chosenTypeId, chosenTherapistId, issuedBy, chosenMeetingId, forceBook]);

  return <SchedulingFlow api={api} invalidLink={!patientUuid} />;
};

export default PatientSchedulePage;
